// src/components/communs/Evaluation.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    X, Clock, ChevronLeft, ChevronRight,
    Maximize2, Minimize2, CheckCircle, XCircle,
    ChevronDown, ChevronUp, AlertCircle, Send, HelpCircle,
    Award, BarChart3, RefreshCw
} from 'lucide-react';
import { APP_CONFIG } from '../../config/appConfig';

// ⭐ Seuils configurables
const INACTIVITY_LIMIT_MS       = 30 * 60 * 1000;   // 30 min sans interaction
const INACTIVITY_CHECK_MS       = 30 * 1000;        // vérification toutes les 30 s
const FOCUS_LOSS_GRACE_MS       = 15 * 1000;        // tolérance alt-tab avant fermeture
const LOCAL_SAVE_INTERVAL_MS    = 30 * 1000;        // sauvegarde locale d'audit

// ⭐ Récupération souple du token : prop d'abord, puis storages usuels.
const resolveAuthToken = (propToken) => {
    if (propToken && typeof propToken === 'string' && propToken.length > 10) return propToken;
    try {
        return (
            localStorage.getItem('token') ||
            localStorage.getItem('accessToken') ||
            localStorage.getItem('jwt') ||
            sessionStorage.getItem('token') ||
            sessionStorage.getItem('accessToken') ||
            sessionStorage.getItem('jwt') ||
            null
        );
    } catch {
        return null;
    }
};

export default function Evaluation({
    module,
    evaluationData,
    formationTitre,
    moduleOrdre,
    totalModules,
    onClose,
    isOpen,
    onComplete,
    onSubmit,
    onSubmitSuccess,
    evalType = 'DIAGNOSTIC',
    evaluationId,
    moduleId,
    etudiantId,
    mode = 'production',
    token,
    submitError = null,
    submitLoading = false,
    onRetrySubmit = null
}) {
    // ⭐ DÉCISION UNIQUE de routage : evaluationId prioritaire.
    const routingMode = evaluationId ? 'evaluation' : (moduleId ? 'module' : null);
    const targetId = evaluationId || moduleId || null;

    // ---------- États ----------
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState({});
    const [timeSpent, setTimeSpent] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [results, setResults] = useState(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showWelcome, setShowWelcome] = useState(true);
    const [currentEvalIndex, setCurrentEvalIndex] = useState(0);
    const [examStarted, setExamStarted] = useState(false);
    const [questionFeedback, setQuestionFeedback] = useState({});

    const [sessionDateLimite, setSessionDateLimite] = useState(null);
    // eslint-disable-next-line
    const [sessionDateDebut, setSessionDateDebut] = useState(null);
    const [remainingSeconds, setRemainingSeconds] = useState(0);
    const [isSessionLoaded, setIsSessionLoaded] = useState(false);
    const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false);

    const [startError, setStartError] = useState(null);
    const [startRetryCount, setStartRetryCount] = useState(0);

    // ---------- Refs ----------
    const containerRef = useRef(null);
    const timerRef = useRef(null);
    const lastActivityRef = useRef(Date.now());
    const hasSubmittedRef = useRef(false);
    const suppressFocusCheckRef = useRef(false);   // true pendant un alert()
    const shuffleCacheRef = useRef({});

    // ---------- Cache d'ordre aléatoire stable ----------
    const getShuffledIndices = (cacheKey, length) => {
        if (!shuffleCacheRef.current[cacheKey] || shuffleCacheRef.current[cacheKey].length !== length) {
            const order = Array.from({ length }, (_, i) => i);
            for (let i = order.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [order[i], order[j]] = [order[j], order[i]];
            }
            shuffleCacheRef.current[cacheKey] = order;
        }
        return shuffleCacheRef.current[cacheKey];
    };
    const shuffleStable = (cacheKey, items) => {
        if (!Array.isArray(items) || items.length < 2) return items || [];
        const order = getShuffledIndices(cacheKey, items.length);
        return order.map(i => items[i]);
    };

    // ---------- Normalisation des données ----------
    let evaluations = [];
    if (Array.isArray(evaluationData)) evaluations = evaluationData;
    else if (evaluationData) evaluations = [evaluationData];

    const normalizedEvaluations = evaluations.map(evalItem => {
        if (evalItem.questions && Array.isArray(evalItem.questions)) return evalItem;
        return {
            titre: evalItem.titre || 'Évaluation',
            description: evalItem.description || '',
            questions: evalItem.questions || (Array.isArray(evalItem) ? evalItem : [evalItem]),
            scoreMinimumPourValider: evalItem.scoreMinimumPourValider || 70
        };
    });

    let finalEvaluations = normalizedEvaluations;
    if (finalEvaluations.length === 0 && evaluationData) {
        if (evaluationData.questions && Array.isArray(evaluationData.questions)) {
            finalEvaluations = [{
                titre: evaluationData.titre || 'Évaluation',
                description: evaluationData.description || '',
                questions: evaluationData.questions,
                scoreMinimumPourValider: evaluationData.scoreMinimumPourValider || 70
            }];
        }
    }
    if (finalEvaluations.length === 0 && Array.isArray(evaluationData) && evaluationData.length > 0) {
        if (evaluationData[0].enonce) {
            finalEvaluations = [{
                titre: 'Évaluation',
                description: '',
                questions: evaluationData,
                scoreMinimumPourValider: 70
            }];
        }
    }

    const currentEval = finalEvaluations[currentEvalIndex] || {};
    const questions = currentEval?.questions || [];
    const totalQuestions = finalEvaluations.reduce((acc, e) => acc + (e.questions?.length || 0), 0);
    const isMultipleEvaluations = finalEvaluations.length > 1;

    // ⭐ Identifiant stable pour la sauvegarde locale (audit)
    const storageKey = evaluationId
        ? `eval_progress_eval_${etudiantId || 'anon'}_${evaluationId}`
        : (module?.id ? `eval_progress_mod_${etudiantId || 'anon'}_${module.id}` : null);

    const clearLocalSave = useCallback(() => {
        if (!storageKey) return;
        try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
    }, [storageKey]);

    // ============================================================
    // CHANGEMENT 1 — Reset COMPLET à chaque ouverture (prod + démo)
    // ============================================================
    useEffect(() => {
        if (!isOpen) return;

        // Purge de tout résidu d'une session précédente (audit)
        clearLocalSave();

        // Reset total de l'état
        setCurrentQuestion(0);
        setCurrentEvalIndex(0);
        setAnswers({});
        setTimeSpent(0);
        setShowWelcome(true);
        setExamStarted(false);
        setIsSubmitted(false);
        setResults(null);
        setShowFeedback(false);
        setQuestionFeedback({});
        setSessionDateLimite(null);
        setSessionDateDebut(null);
        setRemainingSeconds(0);
        setIsSessionLoaded(false);
        setHasAutoSubmitted(false);
        setStartError(null);
        setIsPaused(false);

        // Reset des refs
        hasSubmittedRef.current = false;
        lastActivityRef.current = Date.now();
        shuffleCacheRef.current = {};
    }, [isOpen, evaluationId, module?.id, clearLocalSave]);

    // ============================================================
    // /demarrer au démarrage de l'examen
    // ============================================================
    useEffect(() => {
        if (!isOpen || !examStarted || isSessionLoaded) return;

        if (mode === 'demo' || !etudiantId) {
            setSessionDateLimite(null);
            setIsSessionLoaded(true);
            return;
        }
        if (!routingMode || !targetId) {
            setStartError("Identifiant d'évaluation manquant. Contactez votre formateur.");
            setIsSessionLoaded(true);
            return;
        }
        const authToken = resolveAuthToken(token);
        if (!authToken) {
            setStartError("Session expirée : reconnectez-vous pour démarrer l'évaluation.");
            setIsSessionLoaded(true);
            return;
        }

        const url = routingMode === 'evaluation'
            ? `${APP_CONFIG.BASE_URL}/api/v1/etudiant/evaluations/${targetId}/demarrer?etudiantId=${etudiantId}`
            : `${APP_CONFIG.BASE_URL}/api/v1/etudiant/modules/${targetId}/evaluation/demarrer?etudiantId=${etudiantId}`;

        const startEvaluation = async () => {
            try {
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json'
                    }
                });
                if (!response.ok) {
                    let detail = `HTTP ${response.status}`;
                    try {
                        const errBody = await response.json();
                        if (errBody?.message || errBody?.error) detail = errBody.message || errBody.error;
                    } catch { /* ignore */ }
                    throw new Error(detail);
                }
                const data = await response.json();
                setStartError(null);
                setSessionDateLimite(data.dateLimite || null);
                setSessionDateDebut(data.dateDebut || null);
                setIsSessionLoaded(true);

                if (data.dateLimite) {
                    const now = new Date();
                    const limit = new Date(data.dateLimite);
                    const remaining = Math.floor((limit - now) / 1000);
                    setRemainingSeconds(remaining > 0 ? remaining : 0);
                }
            } catch (error) {
                setStartError(
                    `Impossible de démarrer l'évaluation côté serveur : ${error.message}. ` +
                    `Sans ce démarrage, la soumission sera refusée.`
                );
                setSessionDateLimite(null);
                setIsSessionLoaded(true);
            }
        };
        startEvaluation();
    }, [isOpen, examStarted, isSessionLoaded, routingMode, targetId, etudiantId, mode, token, startRetryCount]);

    const retryStartEvaluation = () => {
        setStartError(null);
        setIsSessionLoaded(false);
        setStartRetryCount(c => c + 1);
    };

    // ============================================================
    // Soumission (déclarée en useCallback pour les useEffects)
    // ============================================================
    const handleSubmit = useCallback(() => {
        if (hasSubmittedRef.current || totalQuestions === 0) return;
        hasSubmittedRef.current = true;

        let allDetails = [];
        let correctCount = 0;
        let totalPoints = 0;
        let earnedPoints = 0;

        finalEvaluations.forEach((evaluation, evalIdx) => {
            (evaluation.questions || []).forEach((question, qIdx) => {
                const key = `${evalIdx}-${qIdx}`;
                const userAnswer = answers[key];
                const isCorrect = evaluateQuestion(question, userAnswer);
                const points = question.points || 1;

                allDetails.push({
                    evalIndex: evalIdx,
                    questionIndex: qIdx,
                    question,
                    userAnswer,
                    isCorrect,
                    points,
                    earnedPoints: isCorrect ? points : 0
                });

                if (isCorrect) { correctCount++; earnedPoints += points; }
                totalPoints += points;
            });
        });

        const score = totalPoints > 0 ? Math.round((earnedPoints / totalPoints) * 100) : 0;
        const isPassed = score >= (currentEval?.scoreMinimumPourValider || 70);

        setResults({
            score,
            correctCount,
            totalQuestions,
            totalPoints,
            earnedPoints,
            isPassed,
            details: allDetails,
            evalResults: finalEvaluations.map((evaluation, idx) => {
                const qs = evaluation.questions || [];
                const correct = allDetails.filter(r => r.evalIndex === idx && r.isCorrect).length;
                return {
                    titre: evaluation.titre || `Évaluation ${idx + 1}`,
                    correct,
                    total: qs.length,
                    score: qs.length > 0 ? Math.round((correct / qs.length) * 100) : 0
                };
            })
        });

        setIsSubmitted(true);
        setShowFeedback(true);

        if (timerRef.current) clearInterval(timerRef.current);

        // ⭐ Nettoyage de la sauvegarde locale après soumission
        clearLocalSave();

        if (onSubmit) {
            const reponses = [];
            finalEvaluations.forEach((evaluation, evalIdx) => {
                (evaluation.questions || []).forEach((question, qIdx) => {
                    const key = `${evalIdx}-${qIdx}`;
                    const userAnswer = answers[key];
                    const questionType = question.typeQuestion || question.type;

                    let reponsePayload;
                    if (['APPARIE', 'CLASSE', 'CATEGORI'].includes(questionType)) {
                        const userMap = (userAnswer && typeof userAnswer === 'object' && !Array.isArray(userAnswer))
                            ? userAnswer : {};
                        const originalKeys = Object.keys(question.configurationAvancee || {});
                        const orderedValues = originalKeys.map(k => userMap[k] || '');
                        reponsePayload = { questionIndex: qIdx, reponsesTexte: orderedValues };
                    } else if (Array.isArray(userAnswer)) {
                        reponsePayload = { questionIndex: qIdx, reponsesTexte: userAnswer.map(String) };
                    } else if (typeof userAnswer === 'number') {
                        reponsePayload = { questionIndex: qIdx, reponseChoisie: userAnswer };
                    } else {
                        const value = (userAnswer !== undefined && userAnswer !== null) ? String(userAnswer) : '';
                        reponsePayload = { questionIndex: qIdx, reponsesTexte: [value] };
                    }
                    reponses.push(reponsePayload);
                });
            });

            onSubmit({
                mode: routingMode,
                targetId,
                evaluationId: evaluationId || null,
                moduleId: module?.id || null,
                score, correct: correctCount, total: totalQuestions, timeSpent,
                answers,
                reponses
            });
        }

        if (onComplete) onComplete(module?.id);
        if (onSubmitSuccess) onSubmitSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answers, currentEval, finalEvaluations, totalQuestions, timeSpent, onSubmit, onComplete, onSubmitSuccess, routingMode, targetId, evaluationId, module?.id, clearLocalSave]);

    // ---------- Timer principal (chronomètre serveur ou décompte local) ----------
    useEffect(() => {
        if (isOpen && examStarted && !isPaused && !isSubmitted && isSessionLoaded) {
            timerRef.current = setInterval(() => {
                if (sessionDateLimite) {
                    const now = new Date();
                    const limit = new Date(sessionDateLimite);
                    const remaining = Math.floor((limit - now) / 1000);
                    if (remaining <= 0) {
                        if (!hasAutoSubmitted) {
                            setHasAutoSubmitted(true);
                            console.log('⏰ Délai d\'évaluation atteint → soumission forcée');
                            handleSubmit();
                        }
                        clearInterval(timerRef.current);
                    } else {
                        setRemainingSeconds(remaining);
                    }
                } else {
                    setTimeSpent(prev => prev + 1);
                }
            }, 1000);
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [isOpen, examStarted, isPaused, isSubmitted, isSessionLoaded, sessionDateLimite, hasAutoSubmitted, handleSubmit]);

    // ============================================================
    // CHANGEMENT 2 (adapté) — Sauvegarde locale d'audit
    // Conservée pendant la session. Nettoyée après soumission.
    // Aucune RESTAURATION automatique : une évaluation démarre
    // toujours à la question 1.
    // ============================================================
    useEffect(() => {
        if (mode === 'demo') return;
        if (!isOpen || isSubmitted || !storageKey) return;

        const interval = setInterval(() => {
            try {
                localStorage.setItem(storageKey, JSON.stringify({
                    etudiantId,
                    evaluationId,
                    moduleId: module?.id,
                    currentQuestion,
                    currentEvalIndex,
                    answers,
                    timeSpent,
                    savedAt: new Date().toISOString()
                }));
            } catch { /* quota ou navigation privée */ }
        }, LOCAL_SAVE_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [isOpen, isSubmitted, storageKey, currentQuestion, currentEvalIndex, answers, timeSpent, mode, etudiantId, evaluationId, module?.id]);

    // ============================================================
    // CHANGEMENT 3 — Surveillance d'inactivité (30 min)
    // → alerte + soumission forcée (page de résumé)
    // ============================================================
    useEffect(() => {
        if (mode === 'demo') return;
        if (!isOpen || !examStarted || isSubmitted) return;

        const id = setInterval(() => {
            const idleMs = Date.now() - lastActivityRef.current;
            if (idleMs >= INACTIVITY_LIMIT_MS && !hasSubmittedRef.current) {
                clearInterval(id);
                suppressFocusCheckRef.current = true;
                try {
                    window.alert(
                        "Votre session d'évaluation est interrompue après 30 minutes d'inactivité.\n" +
                        "Vos réponses actuelles vont être transmises telles quelles."
                    );
                } finally {
                    suppressFocusCheckRef.current = false;
                }
                handleSubmit();
            }
        }, INACTIVITY_CHECK_MS);

        return () => clearInterval(id);
    }, [isOpen, examStarted, isSubmitted, mode, handleSubmit]);

    // ============================================================
    // CHANGEMENT 3bis — Surveillance de perte de focus (anti-triche)
    // Perte de focus > 15 s pendant l'examen → fermeture forcée.
    // ============================================================
    useEffect(() => {
        if (mode === 'demo') return;
        if (!isOpen || !examStarted || isSubmitted) return;

        let hideTimer = null;

        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                hideTimer = setTimeout(() => {
                    if (suppressFocusCheckRef.current) return;
                    if (hasSubmittedRef.current) return;
                    suppressFocusCheckRef.current = true;
                    try {
                        window.alert(
                            "Votre session d'évaluation a été fermée : vous avez quitté l'écran " +
                            "pendant plus de 15 secondes. Une évaluation doit être passée en continu.\n" +
                            "Vos réponses en cours ont été annulées."
                        );
                    } finally {
                        suppressFocusCheckRef.current = false;
                    }
                    clearLocalSave();
                    onClose?.();
                }, FOCUS_LOSS_GRACE_MS);
            } else {
                if (hideTimer) {
                    clearTimeout(hideTimer);
                    hideTimer = null;
                }
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            if (hideTimer) clearTimeout(hideTimer);
        };
    }, [isOpen, examStarted, isSubmitted, mode, onClose, clearLocalSave]);

    // ---------- Réarmement de l'inactivité ----------
    const markActivity = useCallback(() => {
        lastActivityRef.current = Date.now();
    }, []);

    // ---------- Helpers d'affichage ----------
    const formatTime = (seconds) => {
        if (seconds < 0) seconds = 0;
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    const getAnsweredCount = () => Object.keys(answers).length;
    const getProgress = () => totalQuestions > 0 ? (getAnsweredCount() / totalQuestions) * 100 : 0;
    const isDiagnostic = evalType === 'DIAGNOSTIC';
    const isFormative = evalType === 'FORMATIVE';
    const allowImmediateFeedback = isDiagnostic || isFormative;
    const isCorrectionManuelle = !allowImmediateFeedback;

    const getQuestionTypeLabel = (type) => {
        const labels = {
            'CHOIX_UNIQUE': 'Choix unique',
            'CHOIX_MULTIPLE': 'Choix multiple',
            'VRAI_FAUX': 'Vrai/Faux',
            'TEXTE_TROU': 'Texte à trous',
            'APPARIE': 'Appariement',
            'REP_COURT': 'Réponse courte',
            'REP_LONG': 'Réponse longue',
            'CLASSE': 'Classement',
            'CATEGORI': 'Catégorisation',
            'AUDIO/FILE': 'Audio / Fichier'
        };
        return labels[type] || type;
    };

    // ---------- Correction locale (affichage uniquement) ----------
    const evaluateQuestion = (question, userAnswer) => {
        if (userAnswer === undefined || userAnswer === null) return false;
        const type = question.typeQuestion || question.type;
        const solutions = question.solutions || [];
        const resultats = question.resultats;
        const config = question.configurationAvancee || {};

        switch (type) {
            case 'CHOIX_UNIQUE': {
                const correct = resultats || solutions[0] || '';
                return String(userAnswer) === String(correct);
            }
            case 'CHOIX_MULTIPLE': {
                const correct = Array.isArray(resultats) ? resultats : (Array.isArray(solutions) ? solutions : []);
                const user = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
                if (correct.length !== user.length) return false;
                return JSON.stringify([...user].sort()) === JSON.stringify([...correct].sort());
            }
            case 'VRAI_FAUX':
                return String(userAnswer).toLowerCase() === String(solutions[0] || '').toLowerCase();
            case 'TEXTE_TROU':
            case 'REP_COURT': {
                const userText = String(userAnswer).toLowerCase().trim();
                return solutions.some(s => String(s).toLowerCase().trim() === userText);
            }
            case 'APPARIE':
            case 'CLASSE':
            case 'CATEGORI': {
                const keys = Object.keys(config);
                if (keys.length === 0) return false;
                const userMap = (userAnswer && typeof userAnswer === 'object' && !Array.isArray(userAnswer))
                    ? userAnswer : {};
                return keys.every(k => userMap[k] !== undefined && userMap[k] === config[k]);
            }
            case 'AUDIO/FILE':
                return !!userAnswer && userAnswer.trim() !== '';
            case 'REP_LONG':
                return String(userAnswer).trim().length > 0;
            default:
                return false;
        }
    };

    const evaluateAndShowFeedback = (qIndex, answer) => {
        const question = questions[qIndex];
        if (!question) return;
        const isCorrect = evaluateQuestion(question, answer);
        setQuestionFeedback(prev => ({
            ...prev,
            [`${currentEvalIndex}-${qIndex}`]: {
                isCorrect,
                explication: question.explication || ''
            }
        }));
    };

    // ---------- Handlers (avec markActivity) ----------
    const handleAnswer = (questionIndex, value) => {
        if (isSubmitted) return;
        markActivity();
        const key = `${currentEvalIndex}-${questionIndex}`;
        setAnswers(prev => ({ ...prev, [key]: value }));
        if (allowImmediateFeedback) evaluateAndShowFeedback(questionIndex, value);
    };

    const handleMultipleAnswer = (questionIndex, value) => {
        if (isSubmitted) return;
        markActivity();
        const key = `${currentEvalIndex}-${questionIndex}`;
        setAnswers(prev => {
            const current = prev[key] || [];
            const newVal = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
            const updated = { ...prev, [key]: newVal };
            if (allowImmediateFeedback) evaluateAndShowFeedback(questionIndex, newVal);
            return updated;
        });
    };

    const goToNext = () => {
        markActivity();
        if (currentQuestion < questions.length - 1) {
            setCurrentQuestion(prev => prev + 1);
        } else if (currentEvalIndex < finalEvaluations.length - 1) {
            setCurrentEvalIndex(prev => prev + 1);
            setCurrentQuestion(0);
        }
    };

    const goToPrev = () => {
        markActivity();
        if (currentQuestion > 0) {
            setCurrentQuestion(prev => prev - 1);
        } else if (currentEvalIndex > 0) {
            const prevEval = finalEvaluations[currentEvalIndex - 1];
            setCurrentEvalIndex(prev => prev - 1);
            setCurrentQuestion((prevEval?.questions?.length || 0) - 1);
        }
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    const getMissingQuestions = () => {
        const missing = [];
        finalEvaluations.forEach((evalItem, evalIdx) => {
            (evalItem.questions || []).forEach((q, qIdx) => {
                const key = `${evalIdx}-${qIdx}`;
                const answer = answers[key];
                let isAnswered = false;
                if (answer !== undefined && answer !== null) {
                    if (Array.isArray(answer)) isAnswered = answer.some(a => a !== null && a !== undefined && a !== '');
                    else if (typeof answer === 'string') isAnswered = answer.trim().length > 0;
                    else if (typeof answer === 'object') isAnswered = Object.values(answer).some(v => v !== null && v !== undefined && v !== '');
                    else isAnswered = true;
                }
                if (!isAnswered) {
                    const previousQuestions = finalEvaluations.slice(0, evalIdx).reduce((acc, e) => acc + (e.questions?.length || 0), 0);
                    const globalIndex = previousQuestions + qIdx + 1;
                    missing.push({ globalIndex, enonce: q.enonce || `Question ${globalIndex}` });
                }
            });
        });
        return missing;
    };

    // ---------- Rendu des options ----------
    const renderOptions = (question, questionIndex) => {
        const type = question.typeQuestion || question.type;
        const key = `${currentEvalIndex}-${questionIndex}`;
        const userAnswer = answers[key];
        const isSubmittedLocal = isSubmitted;
        const questionResult = results?.details?.find(
            d => d.evalIndex === currentEvalIndex && d.questionIndex === questionIndex
        );
        const config = question.configurationAvancee || {};

        const getOptionStyle = (option) => {
            const isSelected = type === 'CHOIX_MULTIPLE'
                ? Array.isArray(userAnswer) && userAnswer.includes(option.id || option)
                : userAnswer === option.id || userAnswer === option;
            const isCorrectOption = questionResult?.isCorrect !== undefined
                ? question.solutions?.includes(option) ||
                  question.solutions?.includes(option.id) ||
                  question.resultats === option ||
                  question.resultats === option.id
                : false;

            if (!isSubmittedLocal || isCorrectionManuelle) {
                return isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50';
            }
            if (isCorrectOption) return 'border-green-500 bg-green-50';
            if (isSelected && !isCorrectOption) return 'border-red-500 bg-red-50';
            return 'border-gray-200';
        };

        const renderOptionContent = (option, optIndex) => {
            const isSelected = type === 'CHOIX_MULTIPLE'
                ? Array.isArray(userAnswer) && userAnswer.includes(option.id || option)
                : userAnswer === option.id || userAnswer === option;
            const isCorrectOption = question.solutions?.includes(option) ||
                                   question.solutions?.includes(option.id) ||
                                   question.resultats === option ||
                                   question.resultats === option.id;

            return (
                <>
                    <span className="text-xs sm:text-sm font-bold text-gray-500 w-5 sm:w-6">
                        {String.fromCharCode(65 + optIndex)}.
                    </span>
                    <span className="text-xs sm:text-base text-gray-800 font-serif">
                        {option.texte || option}
                    </span>
                    {isSubmittedLocal && !isCorrectionManuelle && isCorrectOption && (
                        <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />
                    )}
                    {isSubmittedLocal && !isCorrectionManuelle && isSelected && !isCorrectOption && (
                        <XCircle className="w-4 h-4 text-red-500 ml-auto" />
                    )}
                </>
            );
        };

        if (type === 'CHOIX_UNIQUE') {
            const shuffledOptions = shuffleStable(`${key}-options`, question.options || []);
            return shuffledOptions.map((option, idx) => (
                <button
                    key={idx}
                    onClick={() => handleAnswer(questionIndex, option.id || option)}
                    disabled={isSubmittedLocal}
                    className={`w-full text-left p-2 sm:p-4 rounded-lg border-2 transition-all flex items-center gap-2 sm:gap-3 text-xs sm:text-base ${getOptionStyle(option, idx)}`}
                >
                    {renderOptionContent(option, idx)}
                </button>
            ));
        }

        if (type === 'CHOIX_MULTIPLE') {
            const shuffledOptions = shuffleStable(`${key}-options`, question.options || []);
            return shuffledOptions.map((option, idx) => (
                <button
                    key={idx}
                    onClick={() => handleMultipleAnswer(questionIndex, option.id || option)}
                    disabled={isSubmittedLocal}
                    className={`w-full text-left p-2 sm:p-4 rounded-lg border-2 transition-all flex items-center gap-2 sm:gap-3 text-xs sm:text-base ${getOptionStyle(option, idx)}`}
                >
                    {renderOptionContent(option, idx)}
                </button>
            ));
        }

        if (type === 'VRAI_FAUX') {
            const shuffledVF = shuffleStable(`${key}-vf`, ['Vrai', 'Faux']);
            return shuffledVF.map((option, idx) => {
                const isSelected = userAnswer === option;
                const isCorrectOption = question.solutions?.[0] === option;
                let style = 'border-gray-200 hover:bg-gray-50';
                if (isSubmittedLocal && !isCorrectionManuelle) {
                    style = isCorrectOption ? 'border-green-500 bg-green-50' :
                            (isSelected && !isCorrectOption ? 'border-red-500 bg-red-50' : 'border-gray-200');
                } else if (isSelected) {
                    style = 'border-blue-500 bg-blue-50';
                }
                return (
                    <button
                        key={idx}
                        onClick={() => handleAnswer(questionIndex, option)}
                        disabled={isSubmittedLocal}
                        className={`w-full text-left p-2 sm:p-4 rounded-lg border-2 transition-all flex items-center gap-2 sm:gap-3 text-xs sm:text-base ${style}`}
                    >
                        <span className="text-gray-800 font-serif">{option}</span>
                        {isSubmittedLocal && isCorrectOption && (
                            <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />
                        )}
                        {isSubmittedLocal && isSelected && !isCorrectOption && (
                            <XCircle className="w-4 h-4 text-red-500 ml-auto" />
                        )}
                    </button>
                );
            });
        }

        if (['REP_COURT', 'REP_LONG', 'TEXTE_TROU'].includes(type)) {
            return (
                <textarea
                    value={userAnswer || ''}
                    onChange={(e) => handleAnswer(questionIndex, e.target.value)}
                    disabled={isSubmittedLocal}
                    className="w-full p-3 sm:p-4 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none font-serif text-xs sm:text-base text-gray-800"
                    rows={type === 'REP_LONG' ? 6 : 3}
                    placeholder="Écrivez votre réponse ici..."
                />
            );
        }

        if (type === 'APPARIE') {
            const entries = Object.entries(config);
            if (entries.length === 0) {
                return (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
                        <p className="text-yellow-700 font-semibold">⚠️ Appariement non disponible</p>
                        <p className="text-gray-600 text-xs">configurationAvancee est vide pour cette question.</p>
                    </div>
                );
            }
            const shuffledEntries = shuffleStable(`${key}-apparie`, entries);
            const userMap = (userAnswer && typeof userAnswer === 'object' && !Array.isArray(userAnswer)) ? userAnswer : {};
            return shuffledEntries.map(([left, right]) => (
                <div key={left} className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1.5 sm:mb-2">
                    <span className="text-xs sm:text-sm font-serif text-gray-700 min-w-[60px] sm:min-w-[100px]">{left}</span>
                    <span className="text-gray-400">→</span>
                    <input
                        type="text"
                        value={userMap[left] || ''}
                        onChange={(e) => handleAnswer(questionIndex, { ...userMap, [left]: e.target.value })}
                        disabled={isSubmittedLocal}
                        className="flex-1 min-w-[100px] p-2 sm:p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none font-serif text-xs sm:text-base text-gray-800"
                        placeholder="Associer..."
                    />
                    {isSubmittedLocal && !isCorrectionManuelle && (
                        <span>
                            {userMap[left] === right ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : (
                                <XCircle className="w-4 h-4 text-red-500" />
                            )}
                        </span>
                    )}
                </div>
            ));
        }

        if (type === 'CLASSE') {
            const elements = shuffleStable(`${key}-classe-elements`, Object.keys(config));
            const categories = shuffleStable(`${key}-classe-cats`, [...new Set(Object.values(config))]);
            const userMap = (userAnswer && typeof userAnswer === 'object' && !Array.isArray(userAnswer)) ? userAnswer : {};
            return elements.map((element) => (
                <div key={element} className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-serif text-gray-700 min-w-[80px]">{element}</span>
                    <span className="text-gray-400">→</span>
                    <select
                        value={userMap[element] || ''}
                        onChange={(e) => handleAnswer(questionIndex, { ...userMap, [element]: e.target.value })}
                        disabled={isSubmittedLocal}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Choisir une catégorie</option>
                        {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                    {isSubmittedLocal && !isCorrectionManuelle && (
                        <span>
                            {userMap[element] === config[element] ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : userMap[element] ? (
                                <XCircle className="w-4 h-4 text-red-500" />
                            ) : null}
                        </span>
                    )}
                </div>
            ));
        }

        if (type === 'CATEGORI') {
            const elements = shuffleStable(`${key}-categori-elements`, Object.keys(config));
            const categories = shuffleStable(`${key}-categori-cats`, question.options || []);
            const userMap = (userAnswer && typeof userAnswer === 'object' && !Array.isArray(userAnswer)) ? userAnswer : {};
            return elements.map((element) => (
                <div key={element} className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-serif text-gray-700 min-w-[80px]">{element}</span>
                    <span className="text-gray-400">→</span>
                    <select
                        value={userMap[element] || ''}
                        onChange={(e) => handleAnswer(questionIndex, { ...userMap, [element]: e.target.value })}
                        disabled={isSubmittedLocal}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Choisir une catégorie</option>
                        {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                    {isSubmittedLocal && !isCorrectionManuelle && (
                        <span>
                            {userMap[element] === config[element] ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                            ) : userMap[element] ? (
                                <XCircle className="w-4 h-4 text-red-500" />
                            ) : null}
                        </span>
                    )}
                </div>
            ));
        }

        if (type === 'AUDIO/FILE') {
            const maxSize = question.configurationAvancee?.maxFileSize || 5242880;
            const acceptedTypes = question.configurationAvancee?.acceptedTypes || ['audio/*'];
            return (
                <div className="p-3 border-2 border-dashed border-gray-300 rounded-lg text-center">
                    <p className="text-sm text-gray-600">Téléchargez un fichier (audio ou autre)</p>
                    <p className="text-xs text-gray-400">Taille max : {maxSize / 1024 / 1024} Mo</p>
                    <input
                        type="file"
                        accept={acceptedTypes.join(',')}
                        onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) handleAnswer(questionIndex, file.name);
                        }}
                        disabled={isSubmittedLocal}
                        className="mt-2 text-sm"
                    />
                    {userAnswer && <p className="mt-1 text-sm text-gray-600">Fichier sélectionné : {userAnswer}</p>}
                    {isSubmittedLocal && <p className="mt-1 text-sm text-green-600">✅ Fichier soumis</p>}
                </div>
            );
        }

        return <p className="text-xs sm:text-sm text-gray-500">Type de question non supporté</p>;
    };

    const renderQuestion = (question, index) => {
        if (!question) return null;
        const isCorrect = results?.details?.find(
            d => d.evalIndex === currentEvalIndex && d.questionIndex === index
        )?.isCorrect;
        const questionId = question.id || question.titre || `Q${index + 1}`;
        const feedbackKey = `${currentEvalIndex}-${index}`;
        const fb = questionFeedback[feedbackKey];

        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-3 mb-3 sm:mb-4">
                    <span className="bg-red-100 text-red-700 px-2 py-0.5 sm:px-4 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold">
                        {questionId}
                    </span>
                    <span className="text-[10px] sm:text-sm text-gray-500 bg-gray-100 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full">
                        {getQuestionTypeLabel(question.typeQuestion || question.type)}
                    </span>
                    {question.points && (
                        <span className="text-[10px] sm:text-sm text-gray-500 bg-gray-100 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full">
                            {question.points} pt{question.points > 1 ? 's' : ''}
                        </span>
                    )}
                    {isSubmitted && !isCorrectionManuelle && isCorrect !== undefined && (
                        <span className="ml-auto">
                            {isCorrect ? (
                                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-500" />
                            ) : (
                                <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />
                            )}
                        </span>
                    )}
                </div>

                <p className="text-sm sm:text-xl font-serif text-gray-900 mb-4 sm:mb-6 leading-relaxed">
                    {question.enonce}
                </p>

                <div className="space-y-2 sm:space-y-3">
                    {renderOptions(question, index)}
                </div>

                {allowImmediateFeedback && fb && !isSubmitted && (
                    <div className={`mt-3 sm:mt-4 p-2 sm:p-4 rounded-lg border ${fb.isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                        <p className="text-xs sm:text-base font-serif">
                            {fb.isCorrect ? '✅ Correct' : '❌ Incorrect'}
                        </p>
                        {fb.explication && (
                            <p className="text-[10px] sm:text-sm text-gray-700 mt-0.5 sm:mt-1">💡 {fb.explication}</p>
                        )}
                    </div>
                )}

                {isSubmitted && !isCorrectionManuelle && question.explication && (
                    <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-xs sm:text-base text-blue-800 font-serif">💡 {question.explication}</p>
                    </div>
                )}
            </div>
        );
    };

    const renderStartErrorBanner = () => {
        if (!startError) return null;
        return (
            <div className="mb-4 p-3 sm:p-4 bg-red-50 border-2 border-red-300 rounded-lg">
                <div className="flex items-start gap-2 sm:gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm sm:text-base font-semibold text-red-800">
                            Le démarrage de l'évaluation a échoué
                        </p>
                        <p className="text-xs sm:text-sm text-red-700 mt-1">{startError}</p>
                        <button
                            onClick={retryStartEvaluation}
                            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs sm:text-sm font-medium transition"
                        >
                            <RefreshCw className="w-3.5 h-3.5" />
                            Réessayer le démarrage
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderWelcome = () => {
        const isDiag = evalType === 'DIAGNOSTIC';
        const isForm = evalType === 'FORMATIVE';
        let icon, title, desc;
        if (isDiag) {
            icon = <BarChart3 className="w-16 h-16 sm:w-[72px] sm:h-[72px] text-green-800" />;
            title = '📊 Évaluation Diagnostique';
            desc = 'Cette évaluation permet de mesurer vos connaissances initiales.';
        } else if (isForm) {
            icon = <BarChart3 className="w-16 h-16 sm:w-[72px] sm:h-[72px] text-green-800" />;
            title = '📝 Évaluation Formative';
            desc = 'Cette évaluation vous aide à progresser tout au long de votre apprentissage.';
        } else {
            icon = <Award className="w-16 h-16 sm:w-[72px] sm:h-[72px] text-green-800" />;
            title = '📝 Évaluation Sommative / Examen';
            desc = 'Cette évaluation permet de valider vos acquis sur l\'ensemble du module.';
        }

        return (
            <div className="flex flex-col items-center justify-center h-full min-h-[400px] sm:min-h-[500px] text-center p-4 sm:p-8">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-10 max-w-3xl w-full">
                    <div className="flex justify-center mb-4 sm:mb-6">{icon}</div>
                    <h1 className="text-xl sm:text-3xl font-serif font-bold text-gray-900 mb-2 sm:mb-3">{title}</h1>
                    <p className="text-sm sm:text-lg text-gray-900 font-serif mb-4 sm:mb-6">{desc}</p>
                    {currentEval?.description && (
                        <p className="text-xs sm:text-base text-gray-900 font-serif mb-4 sm:mb-6">{currentEval.description}</p>
                    )}
                    <div className="bg-gray-50 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 text-left">
                        <div className="flex justify-between text-xs sm:text-base py-1.5 sm:py-2 border-b border-gray-200">
                            <span className="text-gray-900 font-serif">Questions</span>
                            <span className="text-gray-900 font-bold">{totalQuestions}</span>
                        </div>
                        <div className="flex justify-between text-xs sm:text-base py-1.5 sm:py-2 border-b border-gray-200">
                            <span className="text-gray-900 font-serif">Score minimum</span>
                            <span className="text-gray-900 font-bold">
                                {currentEval?.scoreMinimumPourValider || 70}%
                            </span>
                        </div>
                        {isMultipleEvaluations && (
                            <div className="flex justify-between text-xs sm:text-base py-1.5 sm:py-2">
                                <span className="text-gray-900 font-serif">Évaluations</span>
                                <span className="text-gray-900 font-bold">{finalEvaluations.length}</span>
                            </div>
                        )}
                    </div>
                    {renderStartErrorBanner()}
                    <button
                        onClick={() => {
                            if (totalQuestions === 0) {
                                alert('Cette évaluation ne contient aucune question.');
                                return;
                            }
                            markActivity();
                            setShowWelcome(false);
                            setExamStarted(true);
                        }}
                        className="w-full py-2 sm:py-4 bg-green-800 text-white rounded-lg hover:bg-green-700 transition font-serif font-semibold text-sm sm:text-lg"
                    >
                        Commencer l'évaluation
                    </button>
                </div>
            </div>
        );
    };

    const renderResultsPage = () => {
        if (!results) return null;

        const submitErrorBanner = submitError ? (
            <div className="p-3 sm:p-4 bg-red-50 border-2 border-red-300 rounded-lg">
                <div className="flex items-start gap-2 sm:gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm sm:text-base font-semibold text-red-800">
                            La transmission de vos réponses a échoué
                        </p>
                        <p className="text-xs sm:text-sm text-red-700 mt-1">
                            {typeof submitError === 'string' ? submitError : 'Erreur inconnue côté serveur.'}
                        </p>
                        {onRetrySubmit && (
                            <button
                                onClick={onRetrySubmit}
                                disabled={submitLoading}
                                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg text-xs sm:text-sm font-medium transition"
                            >
                                <RefreshCw className={`w-3.5 h-3.5 ${submitLoading ? 'animate-spin' : ''}`} />
                                {submitLoading ? 'Envoi...' : 'Réessayer l\'envoi'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        ) : null;

        if (isCorrectionManuelle) {
            return (
                <div className="space-y-4 sm:space-y-6">
                    {submitErrorBanner}
                    <div className="bg-white rounded-xl shadow-md p-6 sm:p-10 text-center border-t-4 border-green-800">
                        <div className="flex justify-center mb-4 sm:mb-6">
                            {submitError ? (
                                <XCircle className="w-14 h-14 sm:w-20 sm:h-20 text-red-600" />
                            ) : (
                                <CheckCircle className="w-14 h-14 sm:w-20 sm:h-20 text-green-700" />
                            )}
                        </div>
                        <h2 className="text-lg sm:text-2xl font-serif font-bold text-gray-900 mb-2 sm:mb-3">
                            {submitError ? 'Réponses non transmises' : 'Réponses transmises avec succès'}
                        </h2>
                        <p className="text-sm sm:text-lg text-gray-900 font-serif mb-4 sm:mb-6">
                            {submitError
                                ? 'Vos réponses sont conservées localement. Utilisez le bouton « Réessayer l\'envoi » ci-dessus.'
                                : 'Le résultat de cette évaluation sera disponible après correction par votre formateur.'}
                        </p>
                        <div className="mt-4 sm:mt-6 flex flex-wrap items-center justify-center gap-4 sm:gap-8">
                            <div>
                                <div className="text-xl sm:text-3xl font-bold text-gray-900">{results.totalQuestions}</div>
                                <p className="text-[10px] sm:text-sm text-gray-900">Questions répondues</p>
                            </div>
                            <div className="w-px h-12 sm:h-16 bg-gray-200" />
                            <div>
                                <div className="text-xl sm:text-3xl font-bold text-gray-900">{formatTime(timeSpent)}</div>
                                <p className="text-[10px] sm:text-sm text-gray-900">Temps passé</p>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={() => { clearLocalSave(); onClose?.(); }}
                        className="w-full py-3 sm:py-4 bg-green-800 text-white rounded-lg hover:bg-green-700 transition font-serif font-semibold text-sm sm:text-lg"
                    >
                        {submitError ? 'Fermer' : 'Terminer'}
                    </button>
                </div>
            );
        }

        return (
            <div className="space-y-4 sm:space-y-6">
                {submitErrorBanner}
                <div className="bg-white rounded-xl shadow-md p-4 sm:p-8 text-center border-t-4 border-green-800">
                    <h2 className="text-lg sm:text-2xl font-serif font-bold text-gray-900">
                        {isDiagnostic ? 'Résultats du Diagnostic' :
                         isFormative ? 'Résultats de l\'Évaluation Formative' :
                         "Résultats de l'Évaluation"}
                    </h2>
                    <div className="mt-4 sm:mt-6 flex flex-wrap items-center justify-center gap-4 sm:gap-8">
                        <div>
                            <div className="text-3xl sm:text-5xl font-bold text-green-800">{results.score}%</div>
                            <p className="text-[10px] sm:text-sm text-gray-900">Score</p>
                        </div>
                        <div className="w-px h-12 sm:h-16 bg-gray-200" />
                        <div>
                            <div className="text-xl sm:text-3xl font-bold text-green-600">
                                {results.correctCount}/{results.totalQuestions}
                            </div>
                            <p className="text-[10px] sm:text-sm text-gray-900">Bonnes réponses</p>
                        </div>
                        <div className="w-px h-12 sm:h-16 bg-gray-200" />
                        <div>
                            <div className="text-xl sm:text-3xl font-bold text-gray-900">
                                {sessionDateLimite ? formatTime(remainingSeconds) : formatTime(timeSpent)}
                            </div>
                            <p className="text-[10px] sm:text-sm text-gray-900">
                                {sessionDateLimite ? 'Temps restant' : 'Temps passé'}
                            </p>
                        </div>
                        <div className="w-px h-12 sm:h-16 bg-gray-200" />
                        <div>
                            <div className={`text-xl sm:text-3xl font-bold ${results.isPassed ? 'text-green-600' : 'text-red-600'}`}>
                                {results.isPassed ? '✅ Réussi' : '❌ Échec'}
                            </div>
                            <p className="text-[10px] sm:text-sm text-gray-900">Validation</p>
                        </div>
                    </div>
                </div>

                {isMultipleEvaluations && results.evalResults && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4">
                        <h3 className="text-xs sm:text-sm font-serif font-bold text-gray-900 mb-2 sm:mb-3">Résultats par évaluation</h3>
                        {results.evalResults.map((er, idx) => (
                            <div key={idx} className="flex items-center justify-between p-1.5 sm:p-2 bg-gray-50 rounded-lg">
                                <span className="text-xs sm:text-sm font-serif text-gray-900">{er.titre}</span>
                                <div className="flex items-center gap-2 sm:gap-4">
                                    <span className="text-xs sm:text-sm text-gray-900">{er.correct}/{er.total}</span>
                                    <span className={`text-xs sm:text-sm font-bold ${er.score >= 70 ? 'text-green-600' : 'text-red-600'}`}>
                                        {er.score}%
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <button
                    onClick={() => setShowFeedback(!showFeedback)}
                    className="w-full py-2 sm:py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition font-serif text-xs sm:text-base"
                >
                    {showFeedback ? 'Masquer les explications' : 'Afficher les explications'}
                </button>

                {results.details.map((detail, idx) => (
                    <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                        <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                            <span className={`text-[10px] sm:text-sm font-bold px-2 py-0.5 sm:px-3 sm:py-1 rounded ${detail.isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {detail.isCorrect ? '✅ Correct' : '❌ Incorrect'}
                            </span>
                            <span className="text-xs sm:text-sm text-gray-900">{detail.points} pt{detail.points > 1 ? 's' : ''}</span>
                        </div>
                        <p className="text-xs sm:text-base font-serif text-gray-900">{detail.question.enonce}</p>
                        {showFeedback && detail.question.explication && (
                            <div className="mt-2 sm:mt-3 p-2 sm:p-3 bg-blue-50 rounded border border-blue-200">
                                <p className="text-[10px] sm:text-sm text-blue-800 font-serif">💡 {detail.question.explication}</p>
                            </div>
                        )}
                    </div>
                ))}

                <button
                    onClick={() => { clearLocalSave(); onClose?.(); }}
                    className="w-full py-3 sm:py-4 bg-green-800 text-white rounded-lg hover:bg-green-700 transition font-serif font-semibold text-sm sm:text-lg"
                >
                    Terminer
                </button>
            </div>
        );
    };

    // ---------- Rendu principal ----------
    if (!isOpen || !module) return null;

    const isLastQuestion = currentQuestion === questions.length - 1 &&
                          currentEvalIndex === finalEvaluations.length - 1;
    const isFirstQuestion = currentQuestion === 0 && currentEvalIndex === 0;
    const isWelcome = showWelcome && !isSubmitted && getAnsweredCount() === 0;
    const isResults = isSubmitted;
    const showQuitButton = isLastQuestion && getAnsweredCount() === totalQuestions && !isSubmitted;
    const missingQuestions = getMissingQuestions();
    const displayTime = sessionDateLimite ? formatTime(remainingSeconds) : formatTime(timeSpent);
    const isBlockedByStartError = !!startError;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
            <div
                ref={containerRef}
                className="relative w-full max-w-4xl h-[95vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            >
                {/* Header */}
                <div className="bg-green-800 text-white p-2 sm:p-4 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
                        <button
                            onClick={() => { clearLocalSave(); onClose?.(); }}
                            className="p-1.5 hover:bg-white/20 rounded-lg transition"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <div className="flex items-center gap-1.5 sm:gap-3">
                            {isDiagnostic ? <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-green-200" /> :
                             isFormative ? <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-green-200" /> :
                             <Award className="w-4 h-4 sm:w-5 sm:h-5 text-green-200" />}
                            <span className="font-serif font-bold text-xs sm:text-base truncate">
                                {isDiagnostic ? 'Diagnostic' : isFormative ? 'Formative' : 'Sommative'} - {formationTitre || 'Évaluation'}
                            </span>
                            <span className="text-[8px] sm:text-xs text-white/70 bg-white/20 px-1.5 py-0.5 sm:px-3 sm:py-1 rounded-full">
                                Module {moduleOrdre}/{totalModules}
                            </span>
                            {isMultipleEvaluations && !isSubmitted && !isWelcome && (
                                <span className="text-[8px] sm:text-xs text-white/70 bg-white/20 px-1.5 py-0.5 sm:px-3 sm:py-1 rounded-full">
                                    Éval {currentEvalIndex + 1}/{finalEvaluations.length}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                        {!isWelcome && !isResults && (
                            <span className="flex items-center gap-1 text-xs sm:text-sm font-mono bg-white/20 px-2 py-1 rounded-full">
                                <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                                {displayTime}
                            </span>
                        )}
                        <button
                            onClick={() => { markActivity(); setIsPaused(!isPaused); }}
                            className={`p-1.5 rounded-lg transition ${isPaused ? 'bg-yellow-500/50 hover:bg-yellow-500' : 'hover:bg-white/20'}`}
                        >
                            {isPaused ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                        </button>
                        <button onClick={toggleFullscreen} className="p-1.5 hover:bg-white/20 rounded-lg transition">
                            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-8 bg-gray-50">
                    {isWelcome ? renderWelcome() :
                     isResults ? renderResultsPage() :
                     totalQuestions === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-900">
                            <AlertCircle className="w-12 h-12 sm:w-14 sm:h-14 mb-4 text-gray-300" />
                            <p className="text-xs sm:text-lg font-serif text-gray-900">Aucune question d'évaluation</p>
                        </div>
                     ) : (
                        <div className="space-y-4 sm:space-y-6 max-w-3xl mx-auto">
                            {renderStartErrorBanner()}

                            <div className="flex justify-between text-xs sm:text-base text-gray-900 items-center">
                                <span className="font-serif text-gray-900">
                                    {isMultipleEvaluations ?
                                        `Éval ${currentEvalIndex + 1}/${finalEvaluations.length} - Q${currentQuestion + 1}/${questions.length}` :
                                        `Question ${currentQuestion + 1} / ${totalQuestions}`
                                    }
                                </span>
                                <span className="flex items-center gap-1 sm:gap-2 text-gray-900 bg-gray-100 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-[10px] sm:text-sm">
                                    <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                                    {displayTime}
                                </span>
                            </div>

                            <div className="w-full h-1.5 sm:h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-green-800 rounded-full transition-all duration-500"
                                    style={{ width: `${getProgress()}%` }}
                                />
                            </div>

                            {renderQuestion(questions[currentQuestion], currentQuestion)}

                            {!isSubmitted && isLastQuestion && missingQuestions.length > 0 && (
                                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-red-600 font-serif font-semibold text-sm">
                                        ⚠️ Pas de reponse aux questions :
                                    </p>
                                    <ul className="list-disc list-inside text-red-500 text-sm mt-1">
                                        {missingQuestions.map((q, idx) => (
                                            <li key={idx}>Question {q.globalIndex} : {q.enonce}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="flex items-center gap-2 pt-4 sm:pt-6 border-t border-gray-200">
                                <button
                                    onClick={goToPrev}
                                    disabled={isFirstQuestion}
                                    className="flex-1 px-2 py-2 sm:px-5 sm:py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-30 transition font-serif text-xs sm:text-base text-center truncate"
                                >
                                    <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4 inline mr-0.5 sm:mr-1" /> Précédent
                                </button>

                                {showQuitButton && (
                                    <button
                                        onClick={() => {
                                            if (window.confirm('Voulez-vous vraiment quitter ? Vos réponses seront perdues.')) {
                                                clearLocalSave();
                                                onClose?.();
                                            }
                                        }}
                                        className="flex-1 px-2 py-2 sm:px-5 sm:py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-serif text-xs sm:text-base text-center truncate"
                                    >
                                        Quitter
                                    </button>
                                )}

                                {isLastQuestion ? (
                                    <button
                                        onClick={handleSubmit}
                                        disabled={getAnsweredCount() < totalQuestions || isBlockedByStartError}
                                        title={isBlockedByStartError ? "Démarrage serveur en échec : réessayez d'abord" : undefined}
                                        className="flex-1 px-2 py-2 sm:px-8 sm:py-3 bg-green-800 text-white rounded-lg disabled:opacity-50 transition flex items-center justify-center gap-1 font-serif text-xs sm:text-base text-center truncate"
                                    >
                                        <Send className="w-3 h-3 sm:w-4 sm:h-4" /> Soumettre
                                    </button>
                                ) : (
                                    <button
                                        onClick={goToNext}
                                        className="flex-1 px-2 py-2 sm:px-5 sm:py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-serif text-xs sm:text-base text-center truncate"
                                    >
                                        Suivant <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 inline ml-0.5 sm:ml-1" />
                                    </button>
                                )}
                            </div>

                            {getAnsweredCount() < totalQuestions && (
                                <div className="text-[10px] sm:text-sm text-amber-600 text-center font-serif">
                                    ⚠️ Vous avez répondu à {getAnsweredCount()}/{totalQuestions} questions
                                </div>
                            )}
                        </div>
                     )}
                </div>

                {/* Footer */}
                <div className="bg-gray-50 border-t border-gray-200 p-2 sm:p-3 flex justify-between flex-shrink-0">
                    <span className="text-[10px] sm:text-sm text-gray-900 font-serif">Module {moduleOrdre}/{totalModules}</span>
                    <span className="text-[8px] sm:text-xs text-gray-900">
                        {isSubmitted ? '✅ Terminé' :
                         isDiagnostic ? '📊 Diagnostic' :
                         isFormative ? '📝 Formative' :
                         '📝 Sommative'}
                        {sessionDateLimite && !isSubmitted && ` - ⏳ ${displayTime}`}
                        {routingMode && ` - [${routingMode}:${targetId}]`}
                    </span>
                </div>
            </div>
        </div>
    );
}